/* ==========================================================================
   PJ · Product card — assets/pj-card.js
   One delegated controller for every [data-pjc-card] on the page, whichever
   section rendered it. Safe to include from multiple sections: the guard below
   means it only ever binds once.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__pjcCardInit) return;
  window.__pjcCardInit = true;

  var ACTIVE = 'is-active';
  var canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;

  function closest(el, sel) { return el && el.closest ? el.closest(sel) : null; }
  function imgsOf(card) { return card.querySelectorAll('.pjc__img:not([hidden])'); }
  function indexOf(card) { return parseInt(card.dataset.pjcI || '0', 10); }

  /* Images after the first ship with data-src so a 16-product grid doesn't
     fetch 64 files up front. Hydrate on demand, plus both neighbours. */
  function hydrate(img) {
    if (!img || !img.dataset.src) return;
    img.src = img.dataset.src;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    delete img.dataset.src;
    delete img.dataset.srcset;
  }

  function setSlide(card, i, manual) {
    var list = imgsOf(card);
    var n = list.length;
    if (!n) return;
    i = ((i % n) + n) % n;

    hydrate(list[i]);
    hydrate(list[(i + 1) % n]);
    hydrate(list[(i - 1 + n) % n]);

    var allImages = card.querySelectorAll('.pjc__img');
    for (var k = 0; k < allImages.length; k++) allImages[k].classList.remove(ACTIVE);
    list[i].classList.add(ACTIVE);

    var dotsWrap = card.querySelector('.pjc__dots');
    if (dotsWrap && dotsWrap.querySelectorAll('.pjc__dot').length !== n) {
      dotsWrap.innerHTML = '';
      for (var dotIndex = 0; dotIndex < n; dotIndex++) {
        var dotButton = document.createElement('button');
        dotButton.type = 'button';
        dotButton.className = 'pjc__dot';
        dotButton.dataset.pjcDot = dotIndex;
        dotButton.setAttribute('role', 'tab');
        dotButton.setAttribute('aria-label', 'Image ' + (dotIndex + 1));
        dotsWrap.appendChild(dotButton);
      }
    }
    var dots = card.querySelectorAll('.pjc__dot');
    for (var d = 0; d < dots.length; d++) {
      dots[d].classList.toggle(ACTIVE, d === i);
      dots[d].setAttribute('aria-current', d === i ? 'true' : 'false');
    }

    card.dataset.pjcI = i;
    if (manual) card.dataset.pjcTouched = '1';
  }

  /* ---------- Hover preview ----------
     Preserves the old "hover shows the second image" feel. Once the shopper
     drives the carousel themselves, hover stops hijacking it. */
  document.addEventListener('pointerover', function (e) {
    var card = closest(e.target, '[data-pjc-card]');
    if (!card) return;
    var list = imgsOf(card);
    if (list.length < 2) return;
    hydrate(list[1]);
    if (canHover && !card.dataset.pjcTouched && indexOf(card) === 0) setSlide(card, 1);
  }, { passive: true });

  document.addEventListener('pointerout', function (e) {
    var card = closest(e.target, '[data-pjc-card]');
    if (!card) return;
    if (e.relatedTarget && card.contains(e.relatedTarget)) return;
    if (!card.dataset.pjcTouched) setSlide(card, 0);
  }, { passive: true });

  /* ---------- Arrows, dots, swatches, quick add ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target;

    var prev = closest(t, '[data-pjc-prev]');
    if (prev) {
      e.preventDefault();
      var pc = closest(prev, '[data-pjc-card]');
      setSlide(pc, indexOf(pc) - 1, true);
      return;
    }

    var next = closest(t, '[data-pjc-next]');
    if (next) {
      e.preventDefault();
      var nc = closest(next, '[data-pjc-card]');
      setSlide(nc, indexOf(nc) + 1, true);
      return;
    }

    var dot = closest(t, '[data-pjc-dot]');
    if (dot) {
      e.preventDefault();
      setSlide(closest(dot, '[data-pjc-card]'), parseInt(dot.dataset.pjcDot, 10), true);
      return;
    }

    var sw = closest(t, '.pjc__sw');
    if (sw) {
      e.preventDefault();
      onSwatch(sw);
      return;
    }

    var add = closest(t, '[data-pjc-add]');
    if (add) {
      e.preventDefault();
      e.stopPropagation();
      onQuickAdd(add);
      return;
    }
  });

  /* ---------- Swatch ----------
     Selected state paints instantly; the image only changes once the new one
     has decoded, so there is no half-drawn frame and no visible wait. */
  function onSwatch(sw) {
    var card = closest(sw, '[data-pjc-card]');
    if (!card) return;

    var all = card.querySelectorAll('.pjc__sw');
    for (var i = 0; i < all.length; i++) all[i].classList.toggle(ACTIVE, all[i] === sw);

    var color = sw.dataset.pjcColor;
    if (color) {
      var images = card.querySelectorAll('.pjc__img');
      var hasColorImages = false;
      for (var j = 0; j < images.length; j++) {
        var isSelectedColor = images[j].dataset.pjcColor === color;
        images[j].hidden = !isSelectedColor;
        if (isSelectedColor) hasColorImages = true;
      }
      if (hasColorImages) {
        card.dataset.pjcI = '0';
        setSlide(card, 0, true);
        return;
      }
      for (var k = 0; k < images.length; k++) images[k].hidden = false;
    }

    var goto_i = parseInt(sw.dataset.pjcGoto, 10);
    if (!isNaN(goto_i) && goto_i > -1) {
      setSlide(card, goto_i, true);
      return;
    }

    /* Variant image isn't among the card's slides — swap the active one. */
    var src = sw.dataset.pjcSrc;
    if (!src) return;
    var active = card.querySelector('.pjc__img.' + ACTIVE);
    if (!active || active.dataset.pjcCurrent === src) return;
    active.dataset.pjcCurrent = src;
    card.dataset.pjcTouched = '1';

    var pre = new Image();
    pre.decoding = 'async';
    pre.src = src;
    var apply = function () {
      if (active.dataset.pjcCurrent !== src) return;
      active.removeAttribute('srcset');
      active.removeAttribute('sizes');
      active.src = src;
    };
    if (pre.decode) pre.decode().then(apply).catch(apply);
    else if (pre.complete) apply();
    else { pre.onload = apply; pre.onerror = apply; }
  }

  /* ---------- Quick add ---------- */
  function onQuickAdd(btn) {
    if (btn.tagName === 'A') return;
    btn.classList.add('is-loading');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(btn.dataset.pjcAdd, 10), quantity: 1 })
    })
      .then(function (r) { if (!r.ok) throw new Error('add'); return fetch('/cart.js'); })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        if (btn.dataset.added) btn.textContent = btn.dataset.added;
        setTimeout(function () {
          btn.classList.remove('is-added');
          if (btn.dataset.idle) btn.textContent = btn.dataset.idle;
        }, 1800);
      })
      .catch(function () {
        btn.classList.remove('is-loading');
        window.location.href = '/cart';
      });
  }

  /* ---------- Swipe ----------
     Only on cards marked [data-pjc-swipe]. Cards living inside a horizontally
     scrolling section carousel must NOT opt in, or the two scrollers fight for
     the same gesture. Those use the dots and arrows instead. */
  var sw_card = null, sw_x = 0, sw_y = 0, sw_drag = false;

  document.addEventListener('touchstart', function (e) {
    var media = closest(e.target, '[data-pjc-swipe] .pjc__media');
    if (!media) return;
    sw_card = closest(media, '[data-pjc-card]');
    sw_x = e.touches[0].clientX;
    sw_y = e.touches[0].clientY;
    sw_drag = false;
  }, { passive: true });

  document.addEventListener('touchmove', function (e) {
    if (!sw_card || sw_drag) return;
    var dx = e.touches[0].clientX - sw_x;
    var dy = e.touches[0].clientY - sw_y;
    if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy) * 1.4) sw_drag = true;
  }, { passive: true });

  document.addEventListener('touchend', function (e) {
    var card = sw_card;
    sw_card = null;
    if (!card || !sw_drag) { sw_drag = false; return; }
    sw_drag = false;
    var dx = e.changedTouches[0].clientX - sw_x;
    setSlide(card, indexOf(card) + (dx < 0 ? 1 : -1), true);
    /* A swipe must not also count as a tap on the product link. */
    card.dataset.pjcSuppress = '1';
    setTimeout(function () { delete card.dataset.pjcSuppress; }, 400);
  }, { passive: true });

  document.addEventListener('click', function (e) {
    var link = closest(e.target, '.pjc__link');
    if (!link) return;
    var card = closest(link, '[data-pjc-card]');
    if (card && card.dataset.pjcSuppress) e.preventDefault();
  }, true);

  /* Small API so sections can drive the card (e.g. the grid's lifestyle view toggle). */
  window.PJCard = { setSlide: setSlide };

  /* ---------- Keyboard ---------- */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    var card = closest(document.activeElement, '[data-pjc-card]');
    if (!card || !card.classList.contains('has-multi')) return;
    e.preventDefault();
    setSlide(card, indexOf(card) + (e.key === 'ArrowRight' ? 1 : -1), true);
  });
})();
